<i18n>
{
  "fr": {
    "configuratorProject": {
      "button": {
        "label": "Enregistrer le projet"
      },
      "authModalTitle": "Connectez-vous pour enregistrer votre projet",
      "error": "Une erreur s'est produite pendant la création de votre projet. Veuillez réessayer ou nous contacter."
    }
  }
}
</i18n>

<script>
import {
  ConfiguratorGetters,
  ConfiguratorMutation,
  mapGetters as mapConfiguratorGetters,
  mapMutations as mapConfiguratorSetters,
} from '../../store/modules/configurator';
import ConfiguratorAddToCartUi from './ConfiguratorAddToCartUi.vue';
import WithAuthModal from '../../mixins/WithAuthModal';
import AuthModal from '../Modals/AuthModal.vue';
import CustomerProjectModal from '../Modals/CustomerProjectModal.vue';

export default {
  name: 'ConfiguratorProject',
  components: { CustomerProjectModal, AuthModal, ConfiguratorAddToCartUi },
  mixins: [WithAuthModal],
  data() {
    return {
      isNew: true,
      isSaving: false,
      ready: false,
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      accountUrl: ConfiguratorGetters.GET_ACCOUNT_URL,
      authUrl: ConfiguratorGetters.GET_AUTH_URL,
      customerProjectId: ConfiguratorGetters.GET_CURRENT_CUSTOMER_PROJECT_ID,
      isAuthenticated: ConfiguratorGetters.IS_AUTHENTICATED,
      isDirty: ConfiguratorGetters.IS_CURRENT_CUSTOMER_PROJECT_DIRTY,
    }),
    /**
     * @returns {Array}
     */
    buttonClasses() {
      return [
        'configurator-project__button',
        {
          'configurator-project__button--dirty': this.isFullyDirty,
          'configurator-project__button--ready': this.ready,
          'configurator-project__button--saved':
            !this.isFullyDirty && this.isSaved,
          'configurator-project__button--saving': this.isSaving,
        },
      ];
    },
    /**
     * @returns {boolean}
     */
    isSaved() {
      return this.customerProjectId && !this.isSaving;
    },
    /**
     * @returns {boolean}
     */
    isFullyDirty() {
      return this.isDirty && this.isSaved && !this.isSaving;
    },
  },
  mounted() {
    if (window.ConfiguratorProjectInitialized) {
      this.ready = true;
    } else {
      window.ConfiguratorProjectInitialized = true;
      setTimeout(() => (this.ready = true), 500);
    }
  },
  methods: {
    ...mapConfiguratorSetters({
      setPristine: ConfiguratorMutation.SET_CURRENT_CUSTOMER_PROJECT_PRISTINE,
    }),
    onError() {
      window.alert(this.$t('configuratorProject.error'));
    },
    onSuccess() {
      setTimeout(() => {
        if (this.isNew) {
          window.modalStateObservable.isCustomerProjectModalActive = true;
        }

        this.isSaving = false;
        this.setPristine();

        const url = new URL(window.location);
        url.searchParams.set('customerProjectId', this.customerProjectId);
        window.history.replaceState({}, '', url);
      }, 500);
    },
    save() {
      if (this.isAuthenticated) {
        this.isNew = !this.customerProjectId;
        this.isSaving = true;
      } else {
        this.showAuthModal();
      }
    },
    authCallback() {
      /** @type {ConfiguratorState} */
      const configuratorState = this.$store.state.configurator;
      configuratorState.isAuthenticated = true;

      this.save();
    },
  },
};
</script>

<template>
  <div class="configurator-project">
    <button
      type="button"
      :class="buttonClasses"
      :disabled="isSaving || (isSaved && !isFullyDirty)"
      @click="save"
    >
      <i class="configurator-project__button-state"></i>
      <span
        class="configurator-project__button-label"
        v-text="$t('configuratorProject.button.label')"
      ></span>
    </button>

    <Portal append to="portal-container--app-footer">
      <ConfiguratorAddToCartUi
        v-if="isSaving"
        ref="ui"
        is-project
        @success="onSuccess"
        @error="onError"
      />
    </Portal>

    <Portal append to="portal-container--app-footer">
      <CustomerProjectModal v-if="isAuthenticated" :account-url="accountUrl" />
      <AuthModal
        v-else
        :callback="authCallback"
        :iframe-url="authUrl"
        :title="$t('configuratorProject.authModalTitle')"
      />
    </Portal>
  </div>
</template>

<style scoped lang="scss">
.configurator-project {
  $block-selector: &;
  $border-radius: calc(3.25rem / 2);

  @include customer-project-button($block-selector, 3.5rem, 0.9375rem);

  &__button {
    $element-selector: &;

    &:not(#{$element-selector}--ready) {
      display: none;
    }

    &--saved {
      &#{$element-selector}--dirty {
        &:hover {
          background-color: $color-red-thunderbird;
          border-color: $color-red-thunderbird;
        }
      }
    }
  }
}
</style>
